class WarrantyClaimForm extends HTMLElement {
  constructor() {
    super();
    this.previewUrls = [];
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleFilesChange = this.handleFilesChange.bind(this);
    this.returnWarningVisible = false;
    this.emailCopyInProgress = false;
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.webhookUrl = (this.dataset.webhookUrl || '').trim();
    this.uploadsEnabled = this.dataset.uploadsEnabled === 'true';
    this.maxFiles = Number(this.dataset.maxFiles || 4);
    this.maxFileSizeBytes = Number(this.dataset.maxFileSizeBytes || 0);
    this.successMessage = this.dataset.successMessage || 'Your request has been submitted.';
    this.sendShopifyEmailCopy = this.dataset.sendShopifyEmailCopy === 'true';
    this.returnWindowDays = Number(this.dataset.returnWindowDays || 30);
    this.returnWarningTemplate = this.dataset.returnWarning || '';

    this.referenceInput = this.form.querySelector('[data-claim-reference]');
    this.submittedAtInput = this.form.querySelector('[data-submitted-at]');
    this.bodyInput = this.form.querySelector('[data-claim-body]');
    this.attachmentSummaryInput = this.form.querySelector('[data-attachment-summary]');
    this.intakeSelect = this.form.querySelector('[data-intake-type]');
    this.claimCategoryDerived = this.form.querySelector('[data-claim-category-derived]');
    this.fileInput = this.form.querySelector('[data-claim-files]');
    this.previewList = this.querySelector('[data-file-previews]');
    this.dynamicSuccess = this.querySelector('[data-dynamic-success]');
    this.dynamicSuccessCopy = this.querySelector('[data-success-copy]');
    this.dynamicError = this.querySelector('[data-dynamic-error]');
    this.dynamicErrorCopy = this.querySelector('[data-error-copy]');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.submitLabel = this.form.querySelector('[data-submit-label]');
    this.purchaseDateInput = this.form.querySelector('[data-purchase-date]');
    this.returnWindowWarning = this.form.querySelector('[data-return-window-warning]');
    this.returnWindowWarningText = this.form.querySelector('[data-return-window-warning-text]');
    this.orderPicker = this.form.querySelector('[data-order-picker]');
    this.orderNumberInput = this.form.querySelector('[data-order-number-input]');
    this.productNameInput = this.form.querySelector('[data-product-name-input]');
    this.shippingAddressConfirmation = this.form.querySelector('[data-shipping-address-confirmation]');
    this.updatedShippingAddressShell = this.form.querySelector('[data-updated-shipping-address-shell]');
    this.updatedShippingAddressInput = this.form.querySelector('[data-updated-shipping-address]');

    this.referenceInput.value = this.generateReference();

    this.boundUpdateBody = this.updateClaimBody.bind(this);
    this.boundSyncIntake = this.syncIntakeUi.bind(this);
    this.boundUpdateReturnWarning = this.updateReturnWindowWarning.bind(this);
    this.boundSyncShippingAddress = this.syncShippingAddressUi.bind(this);

    this.form.querySelectorAll('input, select, textarea').forEach((field) => {
      if (field.type === 'file') return;
      field.addEventListener('input', this.boundUpdateBody);
      field.addEventListener('change', this.boundUpdateBody);
    });

    this._onIntakeChange = () => {
      this.referenceInput.value = this.generateReference();
      this.syncIntakeUi();
    };
    this.intakeSelect?.addEventListener('change', this._onIntakeChange);

    this.purchaseDateInput?.addEventListener('change', this.boundUpdateReturnWarning);
    this.purchaseDateInput?.addEventListener('input', this.boundUpdateReturnWarning);

    this.orderPicker?.addEventListener('change', () => this.applyOrderPicker());
    this.shippingAddressConfirmation?.addEventListener('change', this.boundSyncShippingAddress);

    if (this.fileInput) {
      this.fileInput.addEventListener('change', this.handleFilesChange);
    }

    this.form.addEventListener('submit', this.handleSubmit);
    this.syncIntakeUi();
    this.syncShippingAddressUi();
    this.updateClaimBody();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.fileInput?.removeEventListener('change', this.handleFilesChange);
    this.intakeSelect?.removeEventListener('change', this._onIntakeChange);
    this.shippingAddressConfirmation?.removeEventListener('change', this.boundSyncShippingAddress);
    this.clearPreviewUrls();
  }

  getIntakeType() {
    const v = (this.intakeSelect?.value || '').trim().toLowerCase();
    if (v === 'warranty' || v === 'return' || v === 'shipment') return v;
    return '';
  }

  getDerivedClaimMeta() {
    const intake = this.getIntakeType();
    const map = {
      warranty: { label: 'Warranty claim', categoryCode: 'warranty_intake' },
      return: { label: 'Return / refund request', categoryCode: 'return_intake' },
      shipment: { label: 'Shipment / fulfillment issue', categoryCode: 'shipment_intake' },
    };
    return map[intake] || null;
  }

  generateReference() {
    const timestamp = new Date().toISOString().replace(/\D/g, '').slice(0, 14);
    const token = Math.random().toString(36).slice(2, 6).toUpperCase();
    const prefixMap = { warranty: 'LBT-WR', return: 'LBT-RT', shipment: 'LBT-SH' };
    const prefix = prefixMap[this.getIntakeType()] || 'LBT-WR';
    return `${prefix}-${timestamp}-${token}`;
  }

  syncIntakeUi() {
    const meta = this.getDerivedClaimMeta();
    if (this.claimCategoryDerived) {
      this.claimCategoryDerived.value = meta ? meta.label : '';
    }

    this.updateReturnWindowWarning();
    this.updateClaimBody();
  }

  applyOrderPicker() {
    if (!this.orderPicker || !this.orderNumberInput) return;
    const opt = this.orderPicker.selectedOptions[0];
    if (!opt || !opt.value) return;

    this.orderNumberInput.value = opt.value;
    const created = opt.getAttribute('data-created-at');
    if (this.purchaseDateInput && created) {
      this.purchaseDateInput.value = created;
    }
    const hint = opt.getAttribute('data-product-hint');
    if (this.productNameInput && hint && !this.productNameInput.value.trim()) {
      this.productNameInput.value = hint;
    }
    this.updateReturnWindowWarning();
    this.updateClaimBody();
  }

  updateReturnWindowWarning() {
    if (!this.returnWindowWarning || !this.returnWindowWarningText) return;

    if (this.getIntakeType() !== 'return' || !this.purchaseDateInput?.value) {
      this.returnWarningVisible = false;
      this.returnWindowWarning.setAttribute('hidden', '');
      return;
    }

    const picked = new Date(`${this.purchaseDateInput.value}T12:00:00`);
    if (Number.isNaN(picked.getTime())) {
      this.returnWarningVisible = false;
      this.returnWindowWarning.setAttribute('hidden', '');
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const orderDay = new Date(picked);
    orderDay.setHours(0, 0, 0, 0);
    const diffMs = today - orderDay;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > this.returnWindowDays) {
      this.returnWarningVisible = true;
      this.returnWindowWarningText.textContent =
        this.returnWarningTemplate ||
        'Your order date may be outside the standard return window. You can still submit for review.';
      this.returnWindowWarning.removeAttribute('hidden');
    } else {
      this.returnWarningVisible = false;
      this.returnWindowWarning.setAttribute('hidden', '');
    }
  }

  syncShippingAddressUi() {
    if (!this.shippingAddressConfirmation || !this.updatedShippingAddressInput) return;

    const confirmedUnchanged = this.shippingAddressConfirmation.checked;
    this.updatedShippingAddressInput.required = !confirmedUnchanged;
    this.updatedShippingAddressInput.disabled = confirmedUnchanged;
    this.updatedShippingAddressInput.setCustomValidity('');

    if (this.updatedShippingAddressShell) {
      this.updatedShippingAddressShell.toggleAttribute('hidden', confirmedUnchanged);
    }

    this.updateClaimBody();
  }

  formatBytes(bytes) {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  }

  deriveSummary(details) {
    const line = (details || '').trim().split('\n')[0] || '';
    return line.length > 220 ? `${line.slice(0, 217)}...` : line;
  }

  getAttachmentNames() {
    return Array.from(this.fileInput?.files || []).map((file) => file.name);
  }

  ensureSubmittedAt() {
    if (!this.submittedAtInput?.value) {
      this.submittedAtInput.value = new Date().toISOString();
    }
    return this.submittedAtInput?.value || '';
  }

  updateClaimBody() {
    if (!this.bodyInput || !this.referenceInput) return;

    const meta = this.getDerivedClaimMeta();
    if (this.claimCategoryDerived) {
      this.claimCategoryDerived.value = meta ? meta.label : '';
    }

    this.attachmentSummaryInput.value = this.getAttachmentNames().join(', ');

    const data = new FormData(this.form);
    const details = (data.get('contact[issue_details]') || '').toString();
    const intake = data.get('contact[intake_type]') || '';
    const category = data.get('contact[claim_category]') || meta?.label || '';
    const submittedAt = data.get('contact[submitted_at]') || '';
    const photoNames = this.attachmentSummaryInput.value || 'None selected';
    const shippingAddressConfirmed = Boolean(data.get('contact[shipping_address_confirmation]'));
    const updatedShippingAddress = (data.get('contact[updated_shipping_address]') || '').toString();

    const lines = [
      `Claim reference: ${this.referenceInput.value}`,
      `Submitted at: ${submittedAt}`,
      `Request type: ${category || intake}`,
      '',
      'Customer',
      `Name: ${data.get('contact[name]') || ''}`,
      `Email: ${data.get('contact[email]') || ''}`,
      '',
      'Order',
      `Order number: ${data.get('contact[order_number]') || ''}`,
      `Order date: ${data.get('contact[purchase_date]') || ''}`,
      `Product name: ${data.get('contact[product_name]') || ''}`,
      '',
      'Shipping address',
      `Order shipping address still correct: ${shippingAddressConfirmed ? 'Yes' : 'No'}`,
      `Updated shipping address: ${updatedShippingAddress || 'Not provided'}`,
      '',
      'Issue',
      details,
      '',
      `Photos selected: ${photoNames}`,
    ];

    this.bodyInput.value = lines.join('\n');
  }

  clearPreviewUrls() {
    this.previewUrls.forEach((url) => URL.revokeObjectURL(url));
    this.previewUrls = [];
  }

  showMessage(type, message) {
    if (type === 'success' && this.dynamicSuccess && this.dynamicSuccessCopy) {
      this.dynamicError?.setAttribute('hidden', '');
      this.dynamicSuccessCopy.textContent = message;
      this.dynamicSuccess.removeAttribute('hidden');
      this.dynamicSuccess.focus();
    }

    if (type === 'error' && this.dynamicError && this.dynamicErrorCopy) {
      this.dynamicSuccess?.setAttribute('hidden', '');
      this.dynamicErrorCopy.textContent = message;
      this.dynamicError.removeAttribute('hidden');
      this.dynamicError.focus();
    }
  }

  clearMessages() {
    this.dynamicSuccess?.setAttribute('hidden', '');
    this.dynamicError?.setAttribute('hidden', '');
  }

  validateFiles() {
    if (!this.fileInput || !this.uploadsEnabled) return true;

    const files = Array.from(this.fileInput.files || []);
    if (!files.length) {
      this.fileInput.setCustomValidity('');
      return true;
    }

    if (files.length > this.maxFiles) {
      const message = `Please upload no more than ${this.maxFiles} images.`;
      this.fileInput.setCustomValidity(message);
      this.fileInput.reportValidity();
      this.showMessage('error', message);
      return false;
    }

    const oversizeFile = files.find((file) => this.maxFileSizeBytes && file.size > this.maxFileSizeBytes);
    if (oversizeFile) {
      const message = `${oversizeFile.name} is larger than ${this.formatBytes(this.maxFileSizeBytes)}.`;
      this.fileInput.setCustomValidity(message);
      this.fileInput.reportValidity();
      this.showMessage('error', message);
      return false;
    }

    this.fileInput.setCustomValidity('');
    return true;
  }

  renderPreviews() {
    if (!this.previewList || !this.fileInput) return;

    const files = Array.from(this.fileInput.files || []);
    this.previewList.innerHTML = '';
    this.clearPreviewUrls();

    if (!files.length) {
      this.previewList.setAttribute('hidden', '');
      return;
    }

    files.forEach((file) => {
      const item = document.createElement('article');
      item.className = 'warranty-claim__preview';

      const image = document.createElement('img');
      const objectUrl = URL.createObjectURL(file);
      this.previewUrls.push(objectUrl);
      image.src = objectUrl;
      image.alt = file.name;

      const meta = document.createElement('div');
      meta.className = 'warranty-claim__preview-meta';

      const name = document.createElement('strong');
      name.className = 'warranty-claim__preview-name';
      name.textContent = file.name;

      const size = document.createElement('span');
      size.className = 'warranty-claim__preview-size';
      size.textContent = this.formatBytes(file.size);

      meta.appendChild(name);
      meta.appendChild(size);
      item.appendChild(image);
      item.appendChild(meta);
      this.previewList.appendChild(item);
    });

    this.previewList.removeAttribute('hidden');
  }

  handleFilesChange() {
    this.clearMessages();
    this.updateClaimBody();
    if (!this.validateFiles()) return;
    this.renderPreviews();
  }

  buildPayload() {
    const data = new FormData(this.form);
    const details = (data.get('contact[issue_details]') || '').toString();
    const intake = (data.get('contact[intake_type]') || '').toString();
    const meta = this.getDerivedClaimMeta();

    return {
      claimReference: this.referenceInput.value,
      submittedAt: this.ensureSubmittedAt(),
      sourceUrl: window.location.href,
      intakeType: intake,
      returnWindowWarningShown: this.returnWarningVisible,
      customer: {
        name: data.get('contact[name]') || '',
        email: data.get('contact[email]') || '',
        phone: data.get('contact[phone]') || '',
      },
      order: {
        orderNumber: data.get('contact[order_number]') || '',
        purchaseDate: data.get('contact[purchase_date]') || '',
        productName: data.get('contact[product_name]') || '',
        quantityAffected: data.get('contact[quantity_affected]') || '',
      },
      shippingAddress: {
        orderAddressStillCorrect: Boolean(data.get('contact[shipping_address_confirmation]')),
        updatedAddress: data.get('contact[updated_shipping_address]') || '',
      },
      claim: {
        category: data.get('contact[claim_category]') || meta?.label || '',
        categoryCode: meta?.categoryCode || '',
        impact: '',
        context: '',
        preferredResolution: '',
        summary: this.deriveSummary(details),
        details,
        acknowledgement: data.get('contact[policy_acknowledgement]') || '',
        attachments: Array.from(this.fileInput?.files || []).map((file) => ({
          name: file.name,
          size: file.size,
          type: file.type,
        })),
      },
    };
  }

  setSubmitting(isSubmitting) {
    if (!this.submitButton || !this.submitLabel) return;

    this.submitButton.disabled = isSubmitting;
    this.submitLabel.textContent = isSubmitting ? 'Submitting...' : 'Submit request';
  }

  submitShopifyEmailCopy() {
    if (!this.sendShopifyEmailCopy || !this.form) return false;

    const action = this.form.getAttribute('action') || '';
    if (!action.includes('/contact')) return false;

    const submissionModeInput = this.form.querySelector('input[name="contact[submission_mode]"]');
    if (submissionModeInput) {
      submissionModeInput.value = 'Endpoint accepted; Shopify email copy';
    }

    this.emailCopyInProgress = true;
    this.form.requestSubmit();
    return true;
  }

  errorMessageFromResult(result, fallback) {
    if (result && typeof result.detail === 'string' && result.detail.trim()) {
      return result.detail.trim();
    }

    const failedFiles = Array.isArray(result?.attachments_failed) ? result.attachments_failed : [];
    if (failedFiles.length) {
      const names = failedFiles.map((item) => item?.filename).filter(Boolean).join(', ');
      return names
        ? `We received the claim but could not attach every photo (${names}). Please submit again before closing this page.`
        : 'We received the claim but could not attach every photo. Please submit again before closing this page.';
    }

    return fallback;
  }

  isRetryableClaimFailure(response, result) {
    if (!response) return true;
    if ([408, 429, 500, 502, 503, 504].includes(response.status)) return true;
    const detail = typeof result?.detail === 'string' ? result.detail.toLowerCase() : '';
    return response.status === 400 && detail.includes('attachment files were not received');
  }

  async postClaim(formData) {
    const response = await fetch(this.webhookUrl, {
      method: 'POST',
      body: formData,
      headers: {
        Accept: 'application/json, text/plain, */*',
      },
    });

    let result = {};
    try {
      result = await response.clone().json();
    } catch (error) {
      result = {};
    }

    return { response, result };
  }

  async handleSubmit(event) {
    if (this.emailCopyInProgress) return;

    this.ensureSubmittedAt();
    this.updateClaimBody();

    if (!this.webhookUrl) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    this.clearMessages();

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    this.setSubmitting(true);

    try {
      this.referenceInput.value = this.referenceInput.value || this.generateReference();
      this.ensureSubmittedAt();
      this.updateClaimBody();

      if (!this.validateFiles()) return;

      const claimReference = this.referenceInput.value;
      const formData = new FormData(this.form);
      formData.append('claim_reference', claimReference);
      formData.append('claim_payload', JSON.stringify(this.buildPayload()));

      let response;
      let result = {};
      try {
        ({ response, result } = await this.postClaim(formData));
      } catch (error) {
        if (!this.webhookUrl) throw error;
        const retryFormData = new FormData(this.form);
        retryFormData.append('claim_reference', claimReference);
        retryFormData.append('claim_payload', JSON.stringify(this.buildPayload()));
        ({ response, result } = await this.postClaim(retryFormData));
      }

      if (!response.ok && this.isRetryableClaimFailure(response, result)) {
        const retryFormData = new FormData(this.form);
        retryFormData.append('claim_reference', claimReference);
        retryFormData.append('claim_payload', JSON.stringify(this.buildPayload()));
        ({ response, result } = await this.postClaim(retryFormData));
      }

      if (!response.ok) {
        throw new Error(
          this.errorMessageFromResult(result, 'We could not submit right now. Please try again in a moment.')
        );
      }
      const selectedFiles = this.getAttachmentNames();
      const uploadedFiles = Array.isArray(result.attachments_uploaded) ? result.attachments_uploaded : [];
      const failedFiles = Array.isArray(result.attachments_failed) ? result.attachments_failed : [];
      if (failedFiles.length || (selectedFiles.length && uploadedFiles.length < selectedFiles.length)) {
        throw new Error(
          this.errorMessageFromResult(result, 'We received the claim but could not attach every photo. Please submit again before closing this page.')
        );
      }

      if (this.submitShopifyEmailCopy()) return;

      this.form.reset();
      this.referenceInput.value = this.generateReference();
      if (this.submittedAtInput) this.submittedAtInput.value = '';
      this.attachmentSummaryInput.value = '';
      this.syncIntakeUi();
      this.syncShippingAddressUi();
      this.clearPreviewUrls();
      this.previewList?.setAttribute('hidden', '');
      if (this.previewList) this.previewList.innerHTML = '';
      this.showMessage('success', `${this.successMessage} Reference: ${claimReference}.`);
    } catch (error) {
      this.showMessage(
        'error',
        error instanceof Error ? error.message : 'We could not submit right now. Please try again.'
      );
    } finally {
      this.setSubmitting(false);
    }
  }
}

if (!customElements.get('warranty-claim-form')) {
  customElements.define('warranty-claim-form', WarrantyClaimForm);
}
